import type { FC } from 'react';
import * as React from 'react';

import { X } from 'lucide-react';

import { Dialog, DialogClose, DialogContentFullScreen, DialogDescription, DialogTitle } from '@shared/components/ui';
import { cn } from '@shared/utils';

interface FullPageModalProps {
  open: boolean;
  onClose: (open: boolean) => void;
  title: string;
  subtitle?: string;
  titleIcon?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  primaryAction?: React.ReactNode;
  headerActions?: React.ReactNode;
}

export const GenericFullPageModal: FC<FullPageModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  titleIcon,
  children,
  footer,
  primaryAction,
  headerActions,
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose} data-testid="generic-full-page-modal-root">
      <DialogContentFullScreen
        aria-describedby={subtitle ? 'modal-subtitle' : undefined}
        data-testid="generic-full-page-modal-content"
      >
        <div
          className="flex h-16 shrink-0 items-center justify-between border-b bg-background px-0"
          data-testid="generic-full-page-modal-header"
        >
          <div className="flex flex-1 items-center gap-4 pl-6 pr-4 min-w-0">
            {titleIcon && (
              <div
                className="flex h-8 w-8 shrink-0 items-center justify-center text-muted-foreground"
                data-testid="generic-full-page-modal-title-icon"
              >
                {titleIcon}
              </div>
            )}
            <div className="flex flex-col justify-center min-w-0">
              <DialogTitle
                className="text-xl font-semibold leading-none tracking-tight truncate"
                data-testid="generic-full-page-modal-title"
              >
                {title}
              </DialogTitle>
              {subtitle && (
                <span
                  id="modal-subtitle"
                  className="text-xs text-muted-foreground mt-1 truncate"
                  data-testid="generic-full-page-modal-subtitle"
                >
                  {subtitle}
                </span>
              )}
            </div>

            <DialogDescription className="sr-only">{subtitle ?? title}</DialogDescription>
          </div>

          <div className="flex h-full items-center">
            {/* Primary Action (Save/Submit) */}
            {primaryAction && (
              <div className="flex h-full items-center px-4" data-testid="generic-full-page-modal-primary-action">
                {primaryAction}
              </div>
            )}

            {/* Toolbar Actions (Help, Settings, etc) */}
            {headerActions && (
              <div className="flex h-full" data-testid="generic-full-page-modal-header-actions">
                {headerActions}
              </div>
            )}

            <div className="h-full border-l">
              <DialogClose
                className="flex h-full w-16 items-center justify-center transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring"
                data-testid="generic-full-page-modal-close"
              >
                <X className="h-6 w-6 text-muted-foreground hover:text-foreground" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>
          </div>
        </div>

        <div
          className="flex-1 overflow-y-auto scroll-container bg-background/50 p-6 md:p-12"
          data-testid="generic-full-page-modal-scroll-area"
        >
          <div className="mx-auto max-w-4xl" data-testid="generic-full-page-modal-children">
            {children}
          </div>
        </div>

        {footer && (
          <div className="shrink-0 border-t bg-background p-4 md:px-6" data-testid="generic-full-page-modal-footer">
            <div className="flex items-center justify-end gap-2">{footer}</div>
          </div>
        )}
      </DialogContentFullScreen>
    </Dialog>
  );
};

/**
 * Helper component for the Right-Side Toolbar buttons to ensure they match the Figma styling.
 */

interface ModalActionProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  //flexible type that works for both Lucide and Custom SVGs
  icon?: React.ComponentType<{ className?: string }>;
}

export const ModalAction = ({ icon: Icon, className, ...props }: ModalActionProps) => {
  return (
    <div className="h-full border-l border-border" data-testid="generic-full-page-modal-action-root">
      {/* can't use GenericBtn component due to conflicting styles that couldn't be solved in a reasonable manner */}
      <button
        type="button"
        className={cn(
          'group flex h-full w-16 items-center justify-center transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring',
          className,
        )}
        data-testid="generic-full-page-modal-action-btn"
        {...props}
      >
        {Icon && <Icon className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />}
      </button>
    </div>
  );
};
